// ============================================================
// 2段階認証（SMS）
//
// Firebase Authentication の多要素認証を使う。
// Identity Platform へのアップグレードと、SMSによる多要素認証の
// 有効化が前提。どちらもFirebaseコンソール側の設定。
//
// メール確認済みのアカウントでないと登録できない点にも注意。
// ============================================================
import {
  multiFactor,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  getMultiFactorResolver,
} from "firebase/auth";
import { auth } from "../firebase";

/** 2段階認証を登録済みか */
export function hasMfa(user) {
  try {
    return !!user && multiFactor(user).enrolledFactors.length > 0;
  } catch {
    return false;
  }
}

/** 登録済みの電話番号（末尾のみ）。「****5678」のような形で返る。 */
export function enrolledPhoneOf(user) {
  try {
    return multiFactor(user).enrolledFactors[0]?.phoneNumber || "";
  } catch {
    return "";
  }
}

/**
 * 日本の携帯番号を E.164 形式（+81から始まる形）に直す。
 * 「090-1234-5678」「09012345678」のどちらでも受け付ける。
 * すでに + で始まっていれば国番号付きとみなしてそのまま扱う。
 */
export function toE164(input) {
  const s = String(input || "").trim();
  if (s.startsWith("+")) return s.replace(/[^\d+]/g, "");
  const digits = s.replace(/\D/g, "");
  if (!digits) return "";
  return "+81" + (digits.startsWith("0") ? digits.slice(1) : digits);
}

/** 携帯番号として妥当そうか（070/080/090 で始まる11桁） */
export function looksLikeMobile(input) {
  return /^0[789]0\d{8}$/.test(String(input || "").replace(/\D/g, ""));
}

/**
 * 不可視のreCAPTCHAを用意する。SMSの悪用を防ぐためFirebaseが必須としている。
 * 画面から離れるときは clear() すること。同じ要素に二重に作ると例外になる。
 */
export function makeRecaptcha(containerId) {
  return new RecaptchaVerifier(auth, containerId, { size: "invisible" });
}

// ---------- 登録 ----------

/** 登録：確認コードをSMSで送る。戻り値を finishEnroll に渡す。 */
export async function startEnroll(user, phoneNumber, verifier) {
  const session = await multiFactor(user).getSession();
  return new PhoneAuthProvider(auth).verifyPhoneNumber({ phoneNumber, session }, verifier);
}

/** 登録：確認コードを検証して登録を確定する。 */
export async function finishEnroll(user, verificationId, code, label = "携帯電話") {
  const cred = PhoneAuthProvider.credential(verificationId, code);
  await multiFactor(user).enroll(PhoneMultiFactorGenerator.assertion(cred), label);
}

// ---------- ログイン ----------

/** ログイン時に2段階認証を求められたか */
export function isMfaRequired(err) {
  return err?.code === "auth/multi-factor-auth-required";
}

/** ログイン時：エラーから解決用オブジェクトを取り出す */
export function resolverOf(err) {
  return getMultiFactorResolver(auth, err);
}

/** ログイン時：登録済みの番号あてに確認コードを送る */
export async function startChallenge(resolver, verifier) {
  return new PhoneAuthProvider(auth).verifyPhoneNumber(
    { multiFactorHint: resolver.hints[0], session: resolver.session },
    verifier
  );
}

/** ログイン時：確認コードを検証してログインを完了する */
export async function finishChallenge(resolver, verificationId, code) {
  const cred = PhoneAuthProvider.credential(verificationId, code);
  return resolver.resolveSignIn(PhoneMultiFactorGenerator.assertion(cred));
}

// ---------- 画面に出すメッセージ ----------

/** Firebaseのエラーを、利用者に見せる日本語にする。 */
export function mfaErrorMessage(err) {
  switch (err?.code) {
    case "auth/invalid-verification-code":
      return "確認コードが違います。もう一度入力してください。";
    case "auth/code-expired":
      return "確認コードの有効期限が切れました。もう一度送信してください。";
    case "auth/invalid-phone-number":
      return "電話番号の形式が正しくありません。";
    case "auth/too-many-requests":
      return "試行回数が多すぎます。しばらく時間をおいてからお試しください。";
    case "auth/unverified-email":
      return "先にメールアドレスの確認を済ませてください。";
    case "auth/requires-recent-login":
      return "安全のため、いったんログインし直してから設定してください。";
    case "auth/operation-not-allowed":
    case "auth/unsupported-first-factor":
      return "2段階認証がまだ有効化されていません。運営にお問い合わせください。";
    default:
      return `エラーが発生しました。${err?.message || err?.code || ""}`;
  }
}
